import { useId, useRef, type CSSProperties } from "react";
import type { ApiCommerceOffer } from "@bridge-applications/pb-types";
import { useTranslation } from "react-i18next";
import { SimpleDialog } from "../SimpleDialog/SimpleDialog";
import { SoundButton } from "../SoundButton/SoundButton";
import { ButtonVar1 } from "../ButtonVar1/ButtonVar1";
import { BundleOfferImage } from "./components/BundleOfferImage/BundleOfferImage";
import { resolveSupportedLocale } from "../../../logic/i18n/locales";
import { offerDisplayFont } from "../../../logic/helpers/offerDisplayFont";
import { haptics } from "../../../logic/helpers/haptics";
import {
    formatCommercePrice,
    formatIntegerAmount,
} from "../../../logic/helpers/commerce";
import {
    bundleDeadline,
    compactBundleAmount,
    type BundleOfferArtwork,
    type BundleOfferFrame,
    type BundleOfferPrize,
} from "../../../logic/helpers/bundleOfferPresentation";
import { useBundleOfferCountdown } from "../../../logic/hooks/useBundleOfferCountdown";
import countdownIcon from "../../../assets/static/images/commerce/bundle-offers/countdown.webp";
import TelegramStar from "../../../assets/static/images/commerce/beach-coin-offer/tg-star-green-outline.svg?react";
import "./BundleOfferDialog.scss";

function frameStyle(frame: BundleOfferFrame): CSSProperties {
    return {
        left: `calc(${frame.x} * var(--bundle-unit))`,
        top: `calc(${frame.y} * var(--bundle-unit))`,
        width: `calc(${frame.width} * var(--bundle-unit))`,
        height: `calc(${frame.height} * var(--bundle-unit))`,
    };
}

interface BundleOfferStyle extends CSSProperties {
    "--bundle-width": number;
    "--bundle-height": number;
    "--bundle-inset-left": number;
    "--bundle-inset-right": number;
    "--bundle-purchase-top": number;
    "--bundle-purchase-left": number;
    "--bundle-purchase-width": number;
    "--bundle-purchase-height": number;
}

interface BundleOfferDialogProps {
    isOpen: boolean;
    /** Localized title, also used when header artwork cannot load. */
    title: string;
    artwork: BundleOfferArtwork;
    prizes: readonly BundleOfferPrize[];
    price: ApiCommerceOffer["prices"][number] | null;
    availability: "available" | "unavailable" | "expired";
    isBusy: boolean;
    error: string | null;
    /** Optional server-provided window end; renewal is owned by the caller. */
    expiresAt?: string | null;
    /** A server-issued checkout can be recovered after the displayed window ends. */
    canResumeCheckout?: boolean;
    /** Omit to hide the count. A null limit means unlimited and has no fraction. */
    purchaseCounts?: Pick<
        ApiCommerceOffer,
        "purchaseLimitPerUser" | "fulfilledPurchaseCount"
    > & { remainingPurchaseCount?: number | null };
    purchaseLabel?: string;
    onClose: () => void;
    /** Invoice creation, duplicate prevention, polling and grants belong to the checkout owner. */
    onPurchase: () => void;
    onCountdownEnd?: () => void;
}

export function BundleOfferDialog({
    isOpen,
    title,
    artwork,
    prizes,
    price,
    availability,
    isBusy,
    error,
    expiresAt,
    canResumeCheckout = false,
    purchaseCounts,
    purchaseLabel,
    onClose,
    onPurchase,
    onCountdownEnd,
}: BundleOfferDialogProps) {
    const { t, i18n } = useTranslation();
    const locale = resolveSupportedLocale(i18n.resolvedLanguage, i18n.language);
    const titleId = useId();
    const rewardsId = useId();
    const titleRef = useRef<HTMLHeadingElement>(null);
    const { remainingMinutes, expired } = useBundleOfferCountdown(
        isOpen,
        expiresAt,
        onCountdownEnd,
    );
    const limit = purchaseCounts?.purchaseLimitPerUser;
    const remaining =
        limit == null || !purchaseCounts
            ? null
            : (purchaseCounts.remainingPurchaseCount ??
              Math.max(0, limit - purchaseCounts.fulfilledPurchaseCount));
    const starsPrice =
        price?.providerCode === "telegram_stars" &&
        price.paymentCurrencyCode === "XTR"
            ? price
            : null;
    const countdownBlocksPurchase = expired && !canResumeCheckout;
    const unavailable =
        availability !== "available" ||
        countdownBlocksPurchase ||
        remaining === 0 ||
        !starsPrice ||
        prizes.length === 0;
    const buttonLabel = isBusy
        ? t("commerce.checkout.processing")
        : unavailable
          ? t("commerce.chain.unavailable")
          : (purchaseLabel ?? null);
    const priceAmount = starsPrice
        ? formatIntegerAmount(starsPrice.unitAmount, locale)
        : "";
    const statusMessage =
        error ??
        (countdownBlocksPurchase || availability === "expired"
            ? t("commerce.chain.expired")
            : null);
    const countdown =
        remainingMinutes === null
            ? null
            : t("commerce.bundle.countdown", {
                  hours: Math.floor(remainingMinutes / 60).toLocaleString(
                      locale,
                  ),
                  minutes: (remainingMinutes % 60).toLocaleString(locale),
              });
    const availableLabel =
        remaining === null || limit == null
            ? null
            : t("commerce.bundle.available", {
                  remaining: remaining.toLocaleString(locale),
                  total: limit.toLocaleString(locale),
              });
    const panelStyle: BundleOfferStyle = {
        "--bundle-width": artwork.width,
        "--bundle-height": artwork.height,
        "--bundle-inset-left": artwork.sideInsets?.left ?? 29.25,
        "--bundle-inset-right": artwork.sideInsets?.right ?? 29.25,
        "--bundle-purchase-top": artwork.purchase.y,
        "--bundle-purchase-left": artwork.purchase.x,
        "--bundle-purchase-width": artwork.purchase.width,
        "--bundle-purchase-height": artwork.purchase.height,
    };

    return (
        <SimpleDialog
            className="bundle-offer-dialog"
            style={panelStyle}
            isOpen={isOpen}
            onClose={onClose}
            initialFocusRef={titleRef}
            aria-labelledby={titleId}
            aria-describedby={rewardsId}
        >
            {isOpen && (
                <section
                    className="bundle-offer-dialog__panel"
                    aria-busy={isBusy}
                >
                    <BundleOfferImage
                        className="bundle-offer-dialog__background"
                        sources={artwork.background.images}
                        locale={locale}
                        style={frameStyle(artwork.background.frame)}
                        alt=""
                        aria-hidden="true"
                    />
                    <h2
                        className="bundle-offer-dialog__title"
                        id={titleId}
                        ref={titleRef}
                        tabIndex={-1}
                        style={frameStyle(artwork.header.frame)}
                        aria-label={title}
                    >
                        <BundleOfferImage
                            sources={artwork.header.images}
                            locale={locale}
                            alt={title}
                        />
                    </h2>
                    <SoundButton
                        className="bundle-offer-dialog__close"
                        aria-label={t("common.close")}
                        onClick={() => {
                            haptics.impact("light");
                            onClose();
                        }}
                        style={{
                            left: `calc(${artwork.close.frame.x + artwork.close.frame.width / 2} * var(--bundle-unit))`,
                            top: `calc(${artwork.close.frame.y + artwork.close.frame.height / 2} * var(--bundle-unit))`,
                        }}
                    >
                        <img
                            src={artwork.close.image}
                            style={{
                                width: `calc(${artwork.close.frame.width} * var(--bundle-unit))`,
                            }}
                            alt=""
                            draggable={false}
                        />
                    </SoundButton>
                    <ul className="bundle-offer-dialog__prizes" id={rewardsId}>
                        {prizes.map((prize) => {
                            const amount = compactBundleAmount(
                                prize.amount,
                                locale,
                            );
                            return (
                                <li key={prize.key} aria-label={prize.label}>
                                    <img
                                        className="bundle-offer-dialog__prize-image"
                                        src={prize.artwork.image}
                                        style={frameStyle(prize.artwork.frame)}
                                        alt=""
                                        aria-hidden="true"
                                        draggable={false}
                                    />
                                    <span
                                        className="bundle-offer-dialog__prize-amount"
                                        style={{
                                            ...frameStyle(
                                                prize.artwork.amountFrame,
                                            ),
                                            fontSize: `calc(${Math.min(42.5, 230 / Math.max(1, [...amount].length))} * var(--bundle-unit))`,
                                        }}
                                        data-display-font={offerDisplayFont(
                                            amount,
                                            "lilita-one",
                                        )}
                                        dir="ltr"
                                        aria-hidden="true"
                                    >
                                        {amount}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                    <div className="bundle-offer-dialog__footer">
                        <ButtonVar1
                            className="bundle-offer-dialog__purchase"
                            disabled={isBusy || unavailable}
                            onClick={() => {
                                const deadline = bundleDeadline(expiresAt);
                                // A backgrounded tab can resume before the timer effect runs.
                                if (
                                    isBusy ||
                                    unavailable ||
                                    (!canResumeCheckout &&
                                        deadline !== null &&
                                        deadline <= Date.now())
                                )
                                    return;
                                onPurchase();
                            }}
                            aria-label={
                                buttonLabel ??
                                t("commerce.bundle.purchaseAriaLabel", {
                                    title,
                                    price: starsPrice
                                        ? formatCommercePrice(
                                              starsPrice,
                                              locale,
                                          )
                                        : "",
                                })
                            }
                            colors={{
                                containerStroke: "#275014",
                                containerFill:
                                    "linear-gradient(#397513 83%, #355c27)",
                                faceStroke:
                                    "linear-gradient(#aad756, #86bf30 42%, #69a43a)",
                                faceFill: "linear-gradient(#a5d91c, #509f12)",
                            }}
                            content={
                                buttonLabel ? (
                                    <span
                                        className="bundle-offer-dialog__purchase-status"
                                        data-display-font={offerDisplayFont(
                                            buttonLabel,
                                            "lilita-one",
                                        )}
                                    >
                                        {buttonLabel}
                                    </span>
                                ) : (
                                    <span
                                        className="bundle-offer-dialog__purchase-price"
                                        data-display-font={offerDisplayFont(
                                            priceAmount,
                                            "lilita-one",
                                        )}
                                        dir="ltr"
                                        aria-hidden="true"
                                    >
                                        <svg
                                            className="bundle-offer-dialog__purchase-star"
                                            viewBox="0 0 133 129"
                                            aria-hidden="true"
                                        >
                                            <TelegramStar />
                                        </svg>
                                        <span
                                            style={{
                                                fontSize: `calc(${Math.min(39.7297, 180 / Math.max(1, [...priceAmount].length))} * var(--bundle-unit))`,
                                            }}
                                        >
                                            {priceAmount}
                                        </span>
                                    </span>
                                )
                            }
                        />
                        {(countdown || availableLabel) && (
                            <div className="bundle-offer-dialog__metadata">
                                {countdown && (
                                    <p
                                        className="bundle-offer-dialog__countdown"
                                        role="timer"
                                        aria-live="off"
                                        data-display-font={offerDisplayFont(
                                            countdown,
                                            "lilita-one",
                                        )}
                                    >
                                        <img
                                            src={countdownIcon}
                                            alt=""
                                            aria-hidden="true"
                                            draggable={false}
                                        />
                                        <span>{countdown}</span>
                                    </p>
                                )}
                                {availableLabel && (
                                    <p
                                        className="bundle-offer-dialog__availability"
                                        data-display-font={offerDisplayFont(
                                            availableLabel,
                                            "lilita-one",
                                        )}
                                    >
                                        {availableLabel}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                    {statusMessage && (
                        <p className="bundle-offer-dialog__error" role="alert">
                            {statusMessage}
                        </p>
                    )}
                </section>
            )}
        </SimpleDialog>
    );
}
